import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';

import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { Outbound } from '@mui/icons-material';
import {
  getCancellationColor,
  getLastOrderColorFromSeconds,
  parseTimeAgoToDaysOrHours,
  interpolateColor,
} from '../../../../utils/cancellation-rate-color';
import React from 'react';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SortingByColumnState } from '../../../common/generic-table/utils';
import { MerchantWithIcon } from '../../../common/styles/merchant-with-icon';

interface MerchantsListingTableProps {
  merchants: MerchantType[];
  isLoading: boolean;
  sorting: SortingByColumnState;
  setSorting: React.Dispatch<React.SetStateAction<SortingByColumnState>>;
}

function MerchantsListingTable({
  merchants,
  isLoading,
  sorting,
  setSorting,
}: MerchantsListingTableProps) {
  const toggleDirection = (current: string) =>
    current === 'increasing' ? 'decreasing' : 'increasing';

  return (
    <TableContainer style={{ maxHeight: '80%' }}>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <>
          <Table stickyHeader style={{ textAlign: 'center' }}>
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  <b>Name</b>
                </TableCell>
                <TableCell align="center">
                  <b>Phone</b>
                </TableCell>
                <TableCell align="center">
                  <b>Locations</b>
                </TableCell>

                {/* Cancellation Rate Header */}
                <TableCell align="center">
                  <Tooltip
                    title={
                      <div>
                        <b>Cancellation Rate</b>
                        <div style={{ fontSize: '0.8em', marginTop: 4 }}>
                          🟢 &lt;10% Excellent <br />
                          🟠 10–30% Moderate <br />
                          🔴 &gt;30% Concerning
                        </div>
                      </div>
                    }
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSorting((prev) => ({
                          column: 'cancellation_rate',
                          order:
                            prev.column === 'cancellation_rate'
                              ? toggleDirection(prev.order)
                              : 'decreasing',
                        }))
                      }
                    >
                      <b>Cancellation Rate</b>
                      {sorting.column === 'cancellation_rate' ? (
                        sorting.order === 'increasing' ? (
                          <KeyboardArrowUpIcon fontSize="small" style={{ marginLeft: 4 }} />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" style={{ marginLeft: 4 }} />
                        )
                      ) : (
                        <UnfoldMoreIcon fontSize="small" style={{ marginLeft: 4 }} />
                      )}
                    </span>
                  </Tooltip>
                </TableCell>

                {/* Last Order Header */}
                <TableCell align="center">
                  <Tooltip
                    title={
                      <div>
                        <b>Time since last order</b>
                        <div style={{ fontSize: '0.8em', marginTop: 4 }}>
                          🟢 Recent (today) <br />
                          🟠 This week <br />
                          🔴 Over a week
                        </div>
                      </div>
                    }
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSorting((prev) => ({
                          column: 'last_order',
                          order:
                            prev.column === 'last_order'
                              ? toggleDirection(prev.order)
                              : 'decreasing',
                        }))
                      }
                    >
                      <b>Last Order</b>
                      {sorting.column === 'last_order' ? (
                        sorting.order === 'increasing' ? (
                          <KeyboardArrowUpIcon fontSize="small" style={{ marginLeft: 4 }} />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" style={{ marginLeft: 4 }} />
                        )
                      ) : (
                        <UnfoldMoreIcon fontSize="small" style={{ marginLeft: 4 }} />
                      )}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell align="center">
                  <b>Has Account</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {merchants.map((merchant) => (
                <TableRow key={merchant.id}>
                  <TableCell align="center" style={{ cursor: 'pointer' }}>
                    <a href={`/merchant/${merchant.id}`}>
                      <b>
                        <MerchantWithIcon centered merchant={merchant} />
                      </b>
                    </a>
                  </TableCell>
                  <TableCell align="center">{merchant.phone_number}</TableCell>
                  <TableCell align="center">
                    {merchant.shop_locations?.map((loc) => loc.name).join(', ') || '-'}
                  </TableCell>

                  {/* === CANCELLATION RATE === */}
                  <TableCell align="center">
                    <Tooltip title={`${merchant.cancellation_rate ?? 0}% of orders cancelled`}>
                      <div
                        style={{
                          width: 90,
                          margin: '0 auto',
                          textAlign: 'center',
                        }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={merchant.cancellation_rate ?? 0}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getCancellationColor(merchant.cancellation_rate ?? 0),
                            },
                          }}
                        />
                        <div
                          style={{
                            fontSize: '0.8em',
                            marginTop: 4,
                            fontWeight: 'bold',
                            color: getCancellationColor(merchant.cancellation_rate ?? 0),
                          }}
                        >
                          {merchant.cancellation_rate ?? 0}%
                        </div>
                      </div>
                    </Tooltip>
                  </TableCell>

                  {/* === LAST ORDER === */}
                  <TableCell align="center">
                    <Tooltip title={`Last order: ${parseTimeAgoToDaysOrHours(merchant.last_order ?? 0)}`}>
                      <Chip
                        label={
                          merchant.last_order
                            ? parseTimeAgoToDaysOrHours(merchant.last_order)
                            : 'No Orders Yet'
                        }
                        sx={{
                          backgroundColor: getLastOrderColorFromSeconds(merchant.last_order == null ? 1e12 : merchant.last_order),
                          color: 'white',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          minWidth: 90,
                        }}
                      />
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    {merchant.has_usable_password ? (
                      <Chip icon={<CheckCircleOutlineIcon color="success" />} label="Connected" />
                    ) : merchant.initialization_attempted ? (
                      <Chip icon={<Outbound color="warning" />} label="Invited" />
                    ) : (
                      <Chip icon={<CancelIcon color="error" />} label="Not Connected" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </TableContainer>
  );
}

export default MerchantsListingTable;